import React from "react";
import {Text} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {COLORS} from "../../../../constants/constants";

export type AdjustPaymentBodyProps = {
  splitJobDriverName: string;
  originalDriverName: string;
  driverSplitPercentage: string | undefined;
  setDriverSplitPercentage: (value: string | undefined) => void;
};

const AdjustPaymentBody: React.FC<AdjustPaymentBodyProps> = ({
  splitJobDriverName,
  originalDriverName,
  driverSplitPercentage,
  setDriverSplitPercentage
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or a single dot (to allow typing .5)
    if (value === "" || value === ".") {
      setDriverSplitPercentage(value);
      return;
    }

    // Validate as float
    if (/^\d*\.?\d*$/.test(value)) {
      const num = parseFloat(value);
      // Ensure it's in range 0-100
      if (!isNaN(num) && num >= 0 && num <= 100) {
        setDriverSplitPercentage(value);
      }
    }
  };

  return (
    <Container>
      <TopBlock>
        <QuestionText>
          What percentage of the payment would you like to attribute to <DriverName>{splitJobDriverName}</DriverName>?
        </QuestionText>
        <InputWrapper>
          <InputContainer>
            <CustomInput
              data-testid="split-percentage-input"
              placeholder="40"
              value={driverSplitPercentage || ""}
              onChange={handleInputChange}
            />
            <PercentageSymbol>%</PercentageSymbol>
          </InputContainer>
          <HelperText>out of 100%</HelperText>
        </InputWrapper>
      </TopBlock>

      <BottomBlock>
        <InfoText>The remainder of the payment will be paid to the original driver - {originalDriverName}</InfoText>
      </BottomBlock>
    </Container>
  );
};

export default AdjustPaymentBody;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 4px;
`;

const TopBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 28px;
`;

const QuestionText = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  flex: 1;
`;

const DriverName = styled.span`
  color: ${COLORS.BLUE};
  font-weight: 500;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const InputContainer = styled.div`
  position: relative;
  width: 56px;
`;

const CustomInput = styled.input`
  width: 100%;
  height: 32px;
  border-radius: 4px;
  border: 1px solid #c3c3c373;
  box-shadow: none;
  text-align: center;
  padding-right: 18px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${COLORS.BLUE};
  }

  &::placeholder {
    color: #a1a1aa;
  }
`;

const PercentageSymbol = styled.span`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  color: ${COLORS.TEXT_BLACK};
  font-size: 14px;
  pointer-events: none;
  display: flex;
  align-items: center;
  line-height: 1; /* Match input font line height roughly */
`;

const HelperText = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: #797979;
`;

const BottomBlock = styled.div`
  /* Removed divider and top padding */
`;

const InfoText = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  color: ${COLORS.TEXT_BLACK};
`;
